import React from 'react';
import { Trans } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';

/**
 * Renders `children` only when the current user may perform write actions
 * (editor or admin). Viewers get `fallback` (nothing by default).
 *
 * Use this to hide create/run/delete/save controls so viewers see a read-only
 * UI. The backend enforces the same rule, so this is purely UX.
 */
export const WriteGuard = ({ children, fallback = null }) => {
    const { canWrite } = useAuth();
    return canWrite ? <>{children}</> : <>{fallback}</>;
};

/** Renders `children` only for admins (user/role/company management). */
export const AdminOnly = ({ children, fallback = null }) => {
    const { isAdmin } = useAuth();
    return isAdmin ? <>{children}</> : <>{fallback}</>;
};

/**
 * Small inline banner telling viewers the page is read-only. Renders nothing
 * for editors/admins.
 */
export const ReadOnlyBanner = ({ className = '' }) => {
    const { isViewer } = useAuth();
    if (!isViewer) return null;
    return (
        <div
            className={`flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-800 ${className}`}
        >
            <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd" />
            </svg>
            <span>
                <Trans i18nKey="shared-common:writeGuard.readOnlyBanner" components={{ bold: <strong /> }} />
            </span>
        </div>
    );
};
